import { BoldOutlined, ItalicOutlined, LinkOutlined, UnderlineOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Tooltip } from 'antd';
import * as Draft from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import s from './RichTextEditor.module.scss';

type RichTextEditorProps = {
  editorState: Draft.EditorState;
  onChange: (editorState: Draft.EditorState) => void;
  placeholder?: string;
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  editorState,
  onChange,
  placeholder,
}) => {
  const { t } = useTranslation();
  const editorRef = React.useRef<Draft.Editor | null>(null);
  const [linkModalVisible, setLinkModalVisible] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');

  const handleKeyCommand = React.useCallback(
    (command: string, state: Draft.EditorState): Draft.DraftHandleValue => {
      const newState = Draft.RichUtils.handleKeyCommand(state, command);
      if (newState) {
        onChange(newState);
        return 'handled';
      }
      return 'not-handled';
    },
    [onChange]
  );

  const toggleInlineStyle = React.useCallback(
    (style: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onChange(Draft.RichUtils.toggleInlineStyle(editorState, style));
    },
    [editorState, onChange]
  );

  const hasInlineStyle = React.useCallback(
    (style: string): boolean => {
      return editorState.getCurrentInlineStyle().has(style);
    },
    [editorState]
  );

  const handleLinkClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const selection = editorState.getSelection();
      if (!selection.isCollapsed()) {
        setLinkUrl('');
        setLinkModalVisible(true);
      }
    },
    [editorState]
  );

  const handleLinkConfirm = React.useCallback(() => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {
      url: linkUrl,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = Draft.EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });
    onChange(
      Draft.RichUtils.toggleLink(
        newEditorState,
        newEditorState.getSelection(),
        entityKey
      )
    );
    setLinkModalVisible(false);
    setLinkUrl('');
  }, [editorState, linkUrl, onChange]);

  const handleLinkCancel = React.useCallback(() => {
    setLinkModalVisible(false);
    setLinkUrl('');
  }, []);

  const focusEditor = React.useCallback(() => {
    editorRef.current?.focus();
  }, []);

  return (
    <div className={s.richTextEditor} onClick={(e) => e.stopPropagation()}>
      <div className={s.toolbar}>
        <Tooltip title={t('formList.notifications.formatting.bold')}>
          <Button
            type={hasInlineStyle('BOLD') ? 'primary' : 'default'}
            size="small"
            icon={<BoldOutlined />}
            onMouseDown={toggleInlineStyle('BOLD')}
          />
        </Tooltip>
        <Tooltip title={t('formList.notifications.formatting.italic')}>
          <Button
            type={hasInlineStyle('ITALIC') ? 'primary' : 'default'}
            size="small"
            icon={<ItalicOutlined />}
            onMouseDown={toggleInlineStyle('ITALIC')}
          />
        </Tooltip>
        <Tooltip title={t('formList.notifications.formatting.underline')}>
          <Button
            type={hasInlineStyle('UNDERLINE') ? 'primary' : 'default'}
            size="small"
            icon={<UnderlineOutlined />}
            onMouseDown={toggleInlineStyle('UNDERLINE')}
          />
        </Tooltip>
        <Tooltip title={t('formList.notifications.formatting.link')}>
          <Button
            size="small"
            icon={<LinkOutlined />}
            onMouseDown={handleLinkClick}
          />
        </Tooltip>
      </div>
      <div className={s.editorContainer} onClick={focusEditor}>
        <Draft.Editor
          ref={editorRef}
          editorState={editorState}
          onChange={onChange}
          handleKeyCommand={handleKeyCommand}
          placeholder={placeholder}
        />
      </div>
      <Modal
        title={t('formList.notifications.formatting.linkPrompt')}
        open={linkModalVisible}
        onOk={handleLinkConfirm}
        onCancel={handleLinkCancel}
        okText={t('formList.notifications.formatting.addLink')}
        cancelText={t('formList.notifications.formatting.cancel')}
      >
        <Input
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="https://"
          onPressEnter={handleLinkConfirm}
        />
      </Modal>
    </div>
  );
};

// Декоратор для отображения ссылок
const findLinkEntities = (
  contentBlock: Draft.ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: Draft.ContentState
) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === 'LINK'
    );
  }, callback);
};

const LinkComponent: React.FC<{
  contentState: Draft.ContentState;
  entityKey: string;
  children: React.ReactNode;
}> = ({ contentState, entityKey, children }) => {
  const { url } = contentState.getEntity(entityKey).getData();
  return (
    <a href={url} className={s.link}>
      {children}
    </a>
  );
};

export const linkDecorator = new Draft.CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: LinkComponent,
  },
]);

// Утилиты для создания и конвертации EditorState
export const createEmptyEditorState = (): Draft.EditorState => {
  return Draft.EditorState.createEmpty(linkDecorator);
};

export const editorStateToHtml = (editorState: Draft.EditorState): string => {
  const contentState = editorState.getCurrentContent();
  const options = {
    entityStyleFn: (entity: Draft.EntityInstance) => {
      const entityType = entity.getType();
      if (entityType === 'LINK') {
        const data = entity.getData();
        return {
          element: 'a',
          attributes: {
            href: data.url,
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        };
      }
      return undefined;
    },
  };
  return stateToHTML(contentState, options);
};

export const hasContent = (editorState: Draft.EditorState): boolean => {
  const contentState = editorState.getCurrentContent();
  return contentState.hasText();
};

export default RichTextEditor;
